import cs from './locales/cs';
import da from './locales/da';
import de from './locales/de';
import en from './locales/en';
import es from './locales/es';
import fi from './locales/fi';
import fr from './locales/fr';
import it from './locales/it';
import nb from './locales/nb';
import nl from './locales/nl';
import pl from './locales/pl';
import pt from './locales/pt';
import ru from './locales/ru';
import sv from './locales/sv';
import uk from './locales/uk';
import { selectPlural, type PluralForms } from './plurals';

/**
 * Мовний паритет із мобільними клієнтами: 15 мов, en — базова. Словники
 * генеруються з lib/l10n/app_*.arb (tool/gen_web_locales.py).
 *
 * ⚠️ Мова зіставляється лише за КОДОМ, без регіону. У Flutter-клієнті це
 * зроблено так само (`AppLocaleResolution`), і саме тому `de-AT` не
 * відкочується на англійську.
 */
export const SUPPORTED_LOCALES = [
  'en', 'uk', 'de', 'ru', 'it', 'fr', 'es', 'pl', 'nl', 'pt', 'sv', 'nb', 'da', 'cs', 'fi',
] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

const dictionaries = {
  en, uk, de, ru, it, fr, es, pl, nl, pt, sv, nb, da, cs, fi,
} satisfies Record<Locale, Record<string, string>>;

/**
 * Мови без власного словника, але з близькою підтримуваною — як у мобільному
 * клієнті: норвезька в будь-якому варіанті → букмол, словацька → чеська.
 */
const LANGUAGE_ALIASES: Readonly<Record<string, Locale>> = { no: 'nb', nn: 'nb', sk: 'cs' };

export type MessageKey = keyof typeof en;

function isSupported(code: string): code is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(code);
}

/** Обирає мову з переліку браузера; фолбек — англійська. */
export function resolveLocale(preferred: readonly string[] | undefined): Locale {
  for (const tag of preferred ?? []) {
    const raw = tag.toLowerCase().split('-')[0];
    const code = raw === undefined ? undefined : (LANGUAGE_ALIASES[raw] ?? raw);
    if (code !== undefined && isSupported(code)) return code;
  }
  return DEFAULT_LOCALE;
}

export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const languages = navigator.languages.length > 0 ? navigator.languages : [navigator.language];
  return resolveLocale(languages.filter((l): l is string => typeof l === 'string' && l.length > 0));
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/gu, (match, name: string) => {
    const value = values[name];
    return value === undefined ? match : String(value);
  });
}

export interface Translator {
  locale: Locale;
  t: (key: MessageKey, values?: Record<string, string | number>) => string;
  /**
   * Текст із числівником у правильній для мови формі. Мова без власних форм
   * бере англійські — як і решта словника.
   */
  plural: (forms: Partial<Record<Locale, PluralForms>> & { en: PluralForms }, n: number, key?: string) => string;
}

export function createTranslator(locale: Locale): Translator {
  const dictionary = dictionaries[locale];

  return {
    locale,
    t: (key, values) => {
      // Присутність ключа гарантована і типами, і перевіркою паритету на
      // збірці (scripts/check-i18n.mjs), тож захисту в рантаймі тут немає
      // навмисно — він би лише маскував зламану збірку.
      const raw = dictionary[key];
      return values === undefined ? raw : interpolate(raw, values);
    },
    plural: (forms, n, key = 'n') => {
      const own = forms[locale];
      return own === undefined
        ? interpolate(selectPlural('en', forms.en, n), { [key]: n })
        : interpolate(selectPlural(locale, own, n), { [key]: n });
    },
  };
}
